import { useRouter } from 'next/router'
import { useCallback, useState, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Image from 'next/image'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'
import { Spinner } from '@wartek-id/spinner'

import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { SaveToDraftDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SaveToDraftDialog'
import { SubmitConfirmationDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SubmitConfirmationDialog'
import useBeforePopState from 'app/KinerjaShared/hooks/useBeforePopState'
import {
  API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION,
  API_PATH_PELAKSANAAN_PROGRESS,
  useAPIPelaksanaanDocumentPreparation,
  useAPIPelaksanaanProgress,
  useAPIUpsertPelaksanaanDocumentPreparation,
} from 'api/kinerja/useAPIKinerja'
import { useApiGetPelaksanaanSchoolList } from 'api/kinerja/gpk/pelaksanaan'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { generateErrorMessage, getErrorCode } from 'app/KinerjaShared/utils/api'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import { TargetPerilakuForm } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/DiskusiPersiapan/TargetPerilakuForm'
import { JadwalObservasiForm } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/DiskusiPersiapan/JadwalObservasiForm'
import { PageGuardGpkPelaksanaanStatus } from 'app/KinerjaGpkPage/components/PageGuardGpkPelaksanaanStatus'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'

import type { CustomError } from 'utils/customError'
import type { IPelaksanaanDocumentPreparationForm } from 'types/kinerja-pelaksanaan'

interface ObservationStepProps {
  title: string
  description: string
  withIcon?: boolean
}

const ObservationStep = ({
  title,
  description,
  withIcon = false,
}: ObservationStepProps) => (
  <div className="flex-1 flex flex-row gap-3">
    {withIcon && <Icon color="informational">arrow_circle_right</Icon>}
    <div className="max-w-full">
      <Text variant="action" className="!text-blue-60">
        {title}
      </Text>
      <Text variant="body">{description}</Text>
    </div>
  </div>
)

const SubHeader = ({ title }: { title: string }) => (
  <div className="p-6 bg-blue-5">
    <Text variant="heading-sm" as="h3">
      {title}
    </Text>
  </div>
)

const DiskusiPersiapan = () => {
  // -------------------------
  // Hooks & Context
  // -------------------------
  const router = useRouter()
  const toast = useToast()
  const { tipe } = useKinerjaTipe()
  const queryClient = useQueryClient()
  const { periodNo, periodYear } = usePeriodContext()

  // -------------------------
  // Component State
  // -------------------------
  const [dialogOpen, setDialogOpen] = useState<'save-draft' | 'submit' | null>(
    null
  )

  // -------------------------
  // Form State
  // -------------------------
  const formMethods = useForm<IPelaksanaanDocumentPreparationForm>({
    defaultValues: {
      behavioralTargets: [],
      behavioralTargetEffort: '',
      observationDate: '',
      observationLocation: {
        npsn: '',
      },
      observationTool: '',
      observationNote: '',
      observationDocuments: [],
    },
  })

  const { getValues, reset, formState } = formMethods

  // -------------------------
  // API Data Fetching
  // -------------------------
  const {
    data: pelaksanaanProgressData,
    isLoading: isLoadingPelaksanaanProgress,
  } = useAPIPelaksanaanProgress({
    tipe,
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
      refetchOnMount: false, // Prevent double fetch on re-render after invalidation
      refetchOnWindowFocus: false, // Prevent double fetch on window focus
    },
  })

  const { data: schoolListData, isLoading: isLoadingSchoolList } =
    useApiGetPelaksanaanSchoolList({
      options: {
        useErrorBoundary: false,
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const { data: documentData, isLoading: isLoadingDocumentPreparation } =
    useAPIPelaksanaanDocumentPreparation({
      tipe,
      opts: {
        enabled: !isLoadingSchoolList,
        useErrorBoundary: false,
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // Sync Persiapan data with form
  useEffect(() => {
    const data = documentData?.data

    if (!data) return

    let npsn = data.detail.observationLocation?.npsn || ''
    // If the school npsn from document detail is not found in the school list,
    // we set it to an empty string to avoid uncontrolled to controlled input warning

    if (npsn && !schoolListData?.data?.some((school) => school.npsn === npsn)) {
      npsn = ''
    }

    reset({
      ...data.detail,
      observationLocation: { npsn },
      behavioralTargets: data.detail.behavioralTargets.map((item) => item.id),
    })
  }, [documentData?.data, reset, schoolListData?.data])

  // -------------------------
  // Mutations
  // -------------------------
  const submitDocumentMutation = useAPIUpsertPelaksanaanDocumentPreparation()

  // -------------------------
  // Derived State
  // -------------------------
  const isSubmitted = documentData?.data?.state === 'SUBMITTED'
  const isEvaluated =
    pelaksanaanProgressData?.data?.evaluationDocumentState === 'READY'
  const isSubmitting = submitDocumentMutation.isLoading

  const isLoadingData =
    isLoadingPelaksanaanProgress ||
    isLoadingDocumentPreparation ||
    isLoadingSchoolList
  const isSaveDraftDisabled = !formState.isDirty || isSubmitting
  const shouldDisableInputs = isSubmitted || isEvaluated || isSubmitting

  // -------------------------
  // Handlers
  // -------------------------
  const closeDialog = useCallback(() => {
    setDialogOpen(null)
  }, [])

  const handleSubmit = useCallback(() => {
    submitDocumentMutation.mutate(
      {
        state: 'SUBMITTED',
        detail: getValues(),
        tipe: 'GPK',
      },
      {
        onSuccess: () => {
          // Invalidate without auto-refetch to prevent double fetch
          // React Query will refetch when needed
          // and deduplicate if multiple components share the same query key
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
            refetchActive: false,
            refetchInactive: false,
          })

          queryClient.invalidateQueries(
            API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION
          )

          toast({
            type: 'success',
            message: 'Berhasil kirim Diskusi Persiapan!',
          })

          router.replace(
            `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
          )
        },
        onError: (err: CustomError) => {
          const errorCode = getErrorCode(err)

          toast({
            message: generateErrorMessage(
              'Gagal kirim Diskusi Persiapan. Coba lagi',
              errorCode
            ),
          })
        },
        onSettled: () => {
          closeDialog()
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSaveDraft = useCallback(
    () => {
      submitDocumentMutation.mutate(
        {
          state: 'DRAFT',
          detail: getValues(),
          tipe: 'GPK',
        },
        {
          onSuccess: () => {
            // Invalidate without auto-refetch to prevent double fetch
            // React Query will refetch when needed
            // and deduplicate if multiple components share the same query key
            queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
              refetchActive: false,
              refetchInactive: false,
            })

            queryClient.invalidateQueries(
              API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION
            )

            toast({
              type: 'success',
              message: 'Berhasil simpan draf!',
            })

            router.replace(
              `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
            )
          },
          onError: (err: CustomError) => {
            const errorCode = getErrorCode(err)

            toast({
              message: generateErrorMessage(
                'Gagal simpan draf. Coba lagi',
                errorCode
              ),
            })
          },
          onSettled: () => {
            closeDialog()
          },
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues]
  )

  // -----------------------------------------
  // Handle guard back navigation for unsaved changes
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`,
  })

  const handleGuardBackNavigation = useCallback(() => {
    if (!isSaveDraftDisabled) {
      setDialogOpen('save-draft')

      // Prevent default back navigation
      return false
    }
    // Allow default back navigation
    return true
  }, [isSaveDraftDisabled])

  // Attach the handler to the browser's history back navigation
  useBeforePopState(router, handleGuardBackNavigation)

  return (
    <div className="pt-14 px-8 pb-8">
      <div className="mx-auto max-w-6xl">
        <Header title="Diskusi Persiapan" onBack={handleBack} />

        <PageGuardGpkPelaksanaanStatus
          periodParams={{ skpPeriod: periodNo, skpYear: periodYear }}
        >
          {/* content start from here */}
          {isLoadingData && (
            <div
              className="flex flex-col gap-4 justify-center items-center mt-9"
              aria-busy="true"
            >
              <Spinner data-testid="loading-spinner" />
            </div>
          )}
          {!isLoadingData && (
            <div className="max-w-5xl m-auto">
              <div className="flex flex-col space-y-3 mt-9 bg-white p-6 border rounded border-subdued">
                <Text variant="heading-sm" className="!text-black" as="h2">
                  Langkah-langkah:
                </Text>
                <div className="flex w-full gap-3.5">
                  <ObservationStep
                    title="Diskusi dengan Tim Kinerja"
                    description="Agar lebih mudah isi Diskusi Persiapan."
                  />
                  <ObservationStep
                    title="Bersiap untuk observasi"
                    description="Lengkapi isi Diskusi Persiapan."
                    withIcon
                  />
                  <ObservationStep
                    title="Pelaksanaan Observasi"
                    description="Semangat dan semoga lancar!"
                    withIcon
                  />
                </div>
              </div>
              {/* header */}
              <div className="flex flex-row justify-between items-center mt-9 bg-blue-5 p-6 border-t border-x rounded-t border-subdued">
                <Text variant="heading-lg" as="h2">
                  Diskusi Persiapan
                </Text>
              </div>
              {/* content */}
              <FormProvider {...formMethods}>
                <form
                  onSubmit={formMethods.handleSubmit(() =>
                    setDialogOpen('submit')
                  )}
                  className="flex flex-col bg-white border-x border-b rounded-b border-subdued"
                >
                  {/* first section */}
                  <TargetPerilakuForm
                    disabled={shouldDisableInputs}
                    pelaksanaanProgressData={pelaksanaanProgressData.data}
                    isLoadingPelaksanaanProgress={isLoadingPelaksanaanProgress}
                  />
                  {/* second section */}
                  <SubHeader title="Jadwal Observasi Praktik Kinerja" />
                  <JadwalObservasiForm
                    disabled={shouldDisableInputs}
                    schools={schoolListData?.data || []}
                    isLoadingSchoolList={isLoadingSchoolList}
                    selectedSchool={
                      documentData.data?.detail.observationLocation
                    }
                  />
                  {/* footer */}
                  <div className="p-6 border-t border-subdued">
                    <div className="flex flex-row items-end justify-between bg-gray-10 rounded-lg gap-6">
                      <div className="flex flex-row items-center p-6 gap-3">
                        <Icon className="text-gray-90">info</Icon>
                        <Text variant="heading-sm" className="flex-1">
                          Persiapkan dan diskusikan dengan Tim Kinerja atau
                          Pejabat Penilai Kinerja terkait perangkat pendukung
                          Praktik Kinerja.
                        </Text>
                      </div>
                      <div className="mr-4 flex items-end">
                        <Image
                          src="/images/pengelolaan-kinerja/img-discussion.png"
                          alt=""
                          height={100}
                          width={200}
                        />
                      </div>
                    </div>
                  </div>
                  {!isEvaluated && !isSubmitted && (
                    <div className="p-6 flex flex-row space-x-2.5 justify-end border-t border-subdued">
                      <Button
                        color="white"
                        size="lg"
                        onClick={() => setDialogOpen('save-draft')}
                        disabled={isSaveDraftDisabled}
                        isLoading={isSubmitting}
                      >
                        Simpan draf
                      </Button>
                      <Button size="lg" type="submit" isLoading={isSubmitting}>
                        Kirim
                      </Button>
                    </div>
                  )}
                </form>
              </FormProvider>
            </div>
          )}
          <SaveToDraftDialog
            isLoading={submitDocumentMutation.isLoading}
            isOpen={dialogOpen === 'save-draft'}
            onConfirm={handleSaveDraft}
            onCancel={closeDialog}
          />
          <SubmitConfirmationDialog
            title="Kumpulkan Diskusi Persiapan?"
            confirmCta="Konfirmasi"
            isLoading={submitDocumentMutation.isLoading}
            isOpen={dialogOpen === 'submit'}
            onCancel={closeDialog}
            onConfirm={formMethods.handleSubmit(handleSubmit)}
          />
        </PageGuardGpkPelaksanaanStatus>
      </div>
    </div>
  )
}

export default DiskusiPersiapan
